import React, { useEffect, useState } from "react";
import {
  Container, Row, Col, Card, CardHeader, CardBody, Table,
  Button, Input, Badge, Spinner, InputGroup,
} from "reactstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import controlPlaneApi from "../../../api/controlPlane";

// Per-customer "owed vs actual" provisioning, plus the drift report — so an
// operator can see and repair a stuck customer without reading PM2 logs or the
// DB (finding N2). Backed by the Control Plane operator endpoints.

const STATE_COLOR = {
  active: "success",
  pending: "secondary",
  provisioning: "info",
  failed: "warning",
  drifted: "warning",
  dead: "danger",
  removed: "dark",
};

const readErr = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const fmt = (d) => (d ? new Date(d).toLocaleString() : "—");

const ProvisioningPage = () => {
  const tenant =
    useSelector((s) => s?.clients?.selectedClient?.value) || "cyberior";

  const [drift, setDrift] = useState({ count: 0, items: [] });
  const [driftLoading, setDriftLoading] = useState(false);
  const [customerId, setCustomerId] = useState("");
  const [services, setServices] = useState(null);
  const [svcLoading, setSvcLoading] = useState(false);
  const [retrying, setRetrying] = useState("");

  const loadDrift = async () => {
    setDriftLoading(true);
    try {
      const res = await controlPlaneApi.getDrift(tenant);
      setDrift(res?.data || { count: 0, items: [] });
    } catch (e) {
      toast.error(readErr(e, "Failed to load drift report"));
    } finally {
      setDriftLoading(false);
    }
  };

  const loadServices = async (id) => {
    const cid = id ?? customerId;
    if (!cid) {
      toast.warn("Enter a customer ID first");
      return;
    }
    setSvcLoading(true);
    setServices(null);
    try {
      const res = await controlPlaneApi.getCustomerServices(tenant, cid);
      setServices(res?.data || { customer_id: cid, services: [] });
    } catch (e) {
      toast.error(readErr(e, "Failed to load customer services"));
    } finally {
      setSvcLoading(false);
    }
  };

  const retry = async (slug) => {
    if (!services?.customer_id) return;
    setRetrying(slug);
    try {
      const res = await controlPlaneApi.retryService(tenant, services.customer_id, slug);
      const d = res?.data || {};
      if (d.enqueued) toast.success(`Retry enqueued for ${slug}`);
      else toast.info(`${slug}: ${d.reason || "not enqueued"}`);
      await loadServices(services.customer_id);
      await loadDrift();
    } catch (e) {
      toast.error(readErr(e, "Retry failed"));
    } finally {
      setRetrying("");
    }
  };

  useEffect(() => {
    loadDrift();
    // reload drift whenever the operator switches tenant/client
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant]);

  return (
    <Container fluid className="p-4">
      <Row className="mb-3">
        <Col className="d-flex align-items-center justify-content-between flex-wrap">
          <div>
            <h4 className="mb-1">Provisioning &amp; Drift</h4>
            <span className="text-muted">
              What each customer is owed vs. what the vendor actually has.
            </span>
          </div>
          <Badge color="primary" pill>tenant: {tenant}</Badge>
        </Col>
      </Row>

      {/* Drift report */}
      <Row className="mb-4">
        <Col>
          <Card>
            <CardHeader className="d-flex align-items-center justify-content-between">
              <div>
                <strong>Drift report</strong>
                <span className="text-muted ms-2">entitled but not active</span>
              </div>
              <div className="d-flex align-items-center">
                <Badge color={drift.count ? "danger" : "success"} pill className="me-3">
                  {drift.count} drifting
                </Badge>
                <Button size="sm" color="secondary" outline onClick={loadDrift} disabled={driftLoading}>
                  {driftLoading ? <Spinner size="sm" /> : "Refresh"}
                </Button>
              </div>
            </CardHeader>
            <CardBody>
              {driftLoading ? (
                <div className="text-center py-4"><Spinner /></div>
              ) : drift.items?.length ? (
                <div style={{ overflowX: "auto" }}>
                  <Table hover responsive className="mb-0">
                    <thead>
                      <tr>
                        <th>Customer ID</th>
                        <th>Service</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {drift.items.map((it, i) => (
                        <tr key={`${it.customer_id}-${it.slug}-${i}`}>
                          <td>{it.customer_id}</td>
                          <td><code>{it.slug}</code></td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              color="primary"
                              outline
                              onClick={() => {
                                setCustomerId(String(it.customer_id));
                                loadServices(it.customer_id);
                              }}
                            >
                              View
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-success py-3">
                  No drift — every entitled customer is active. 🎉
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* Per-customer provisioning */}
      <Row>
        <Col>
          <Card>
            <CardHeader><strong>Customer provisioning</strong></CardHeader>
            <CardBody>
              <Row className="mb-3">
                <Col md={5} lg={4}>
                  <InputGroup>
                    <Input
                      type="number"
                      placeholder="Customer ID"
                      value={customerId}
                      onChange={(e) => setCustomerId(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && loadServices()}
                    />
                    <Button color="primary" onClick={() => loadServices()} disabled={svcLoading}>
                      {svcLoading ? <Spinner size="sm" /> : "Load"}
                    </Button>
                  </InputGroup>
                </Col>
              </Row>

              {svcLoading ? (
                <div className="text-center py-4"><Spinner /></div>
              ) : services ? (
                <div style={{ overflowX: "auto" }}>
                  <div className="mb-2 text-muted">
                    Customer <strong>#{services.customer_id}</strong>
                  </div>
                  <Table responsive className="mb-0 align-middle">
                    <thead>
                      <tr>
                        <th>Service</th>
                        <th>Entitled</th>
                        <th>State</th>
                        <th>External ref</th>
                        <th>Attempts</th>
                        <th>Last verified</th>
                        <th>Last error</th>
                        <th className="text-end">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(services.services || []).map((s) => (
                        <tr key={s.slug}>
                          <td>
                            <div><strong>{s.display_name || s.slug}</strong></div>
                            <code className="text-muted">{s.slug}</code>
                          </td>
                          <td>
                            <Badge color={s.entitled ? "success" : "secondary"}>
                              {s.entitled ? "yes" : "ended"}
                            </Badge>
                          </td>
                          <td>
                            <Badge color={STATE_COLOR[s.state] || "light"}>
                              {s.state || "none"}
                            </Badge>
                          </td>
                          <td><code>{s.external_ref || "—"}</code></td>
                          <td>{s.attempts ?? 0}</td>
                          <td className="text-nowrap">{fmt(s.last_verified_at)}</td>
                          <td style={{ maxWidth: 220 }}>
                            <span className="text-danger small">{s.last_error || ""}</span>
                          </td>
                          <td className="text-end">
                            <Button
                              size="sm"
                              color="warning"
                              disabled={s.state === "active" || retrying === s.slug}
                              onClick={() => retry(s.slug)}
                            >
                              {retrying === s.slug ? <Spinner size="sm" /> : "Retry"}
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!(services.services || []).length && (
                        <tr>
                          <td colSpan={8} className="text-muted text-center py-3">
                            No entitlements recorded for this customer.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-muted py-2">
                  Enter a customer ID (or click “View” on a drifting row) to see their provisioning.
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ProvisioningPage;
